import { MobModel } from "types";

import avenPilotImage from "assets/mob-aven-pilot.svg";
import avenShipImage from "assets/mob-aven-ship.svg";
import graverPilotImage from "assets/mob-graver-pilot.svg";
import graverShipImage from "assets/mob-graver-ship.svg";
import radienPilotImage from "assets/mob-radien-pilot.svg";
import radienShipImage from "assets/mob-radien-ship.svg";
import spectraPilotImage from "assets/mob-spectra-pilot.svg";
import spectraShipImage from "assets/mob-spectra-ship.svg";
import wraethPilotImage from "assets/mob-wraeth-pilot.svg";
import wraethShipImage from "assets/mob-wraeth-ship.svg";

import { BASE_STATS } from "data/baseStats";

const { FIREPOWER, RESILIENCE, SPEED, PRECISION, ENERGY } = BASE_STATS;

// SECTOR TIER 2 - MEDALLION RANGE: 21-80

const SENECA_HAVEN_MOBS: Record<string, MobModel> = {
  aven: {
    name: "Aven",
    images: {
      ship: avenShipImage,
      pilot: avenPilotImage,
    },
    dialogs: {
      opening:
        "I think energy mixes well with precision. I need to be able to actually hit you to win.",
      win: "Maybe I should mix energy with something else...",
      lose: "I told you, energy mixes with precision like ...energy mixes with other things. We don't talk about much else besides energy here on Seneca.",
    },
    position: 60,
    heightPercentage: 35,
    medallions: 25,
    baseStatsModifiers: {
      firepower: 2,
      resilience: 3,
      speed: 2,
      precision: 4,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [FIREPOWER, ENERGY, RESILIENCE, PRECISION, SPEED],
    isBoss: false,
    drops: {
      platinum: "lock_core",
      gold: "sharp_core",
      silver: "void_scope",
      bronze: "void_wall",
    },
    installedUpgrades: {
      weapons: null,
      shields: "void_wall",
      thrusters: null,
      targetingSystem: "void_scope",
      reactorCore: "lock_core",
    },
    partPositions: {
      weapons: { x: 70.5, y: 57.25 },
      targeting: { x: 80.5, y: 57.25 },
      thrusters: { x: 20.25, y: 50.25 },
    },
    skills: ["pressurePoint", "energyBlast"],
  },
  spectra: {
    name: "Spectra",
    images: {
      ship: spectraShipImage,
      pilot: spectraPilotImage,
    },
    dialogs: {
      opening:
        "I love my weapons, almost as I love the Core. ...Don't tell the Core.",
      win: "I now hate my weapons. Stupid weapons.",
      lose: "I now love my weapons even more. Lovely weapons.",
    },
    position: 100,
    heightPercentage: 35,
    medallions: 35,
    baseStatsModifiers: {
      firepower: 4,
      resilience: 2,
      speed: 2,
      precision: 3,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 3,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [SPEED, ENERGY, RESILIENCE, PRECISION, FIREPOWER],
    isBoss: false,
    drops: {
      platinum: "striker_nexus",
      gold: "force_nexus",
      silver: "void_cutter",
      bronze: "energy_scope",
    },
    installedUpgrades: {
      weapons: "void_cutter",
      shields: null,
      thrusters: null,
      targetingSystem: "energy_scope",
      reactorCore: "striker_nexus",
    },
    partPositions: {
      weapons: { x: 58, y: 61 },
      targeting: { x: 68, y: 61 },
      thrusters: { x: 20, y: 55.5 },
    },
    skills: ["shieldBreaker", "energyBlast"],
  },
  radien: {
    name: "Radien",
    images: {
      ship: radienShipImage,
      pilot: radienPilotImage,
    },
    dialogs: {
      opening:
        "Let's get this over quickly. Quickly because I'm fast. Faster than you.",
      win: "Well, I guess I'm too slow. I need to process this. Slowly.",
      lose: "That went even quicker than I expected.",
    },
    position: 140,
    heightPercentage: 35,
    medallions: 45,
    baseStatsModifiers: {
      firepower: 3,
      resilience: 2,
      speed: 4,
      precision: 2,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 2,
      precision: 3,
      energy: 1,
    },
    baseStatsTargets: [RESILIENCE, ENERGY, FIREPOWER, PRECISION, SPEED],
    isBoss: false,
    drops: {
      platinum: "flash_nexus",
      gold: "swift_nexus",
      silver: "voidburner",
      bronze: "energy_beam",
    },
    installedUpgrades: {
      weapons: "energy_beam",
      shields: null,
      thrusters: "voidburner",
      targetingSystem: null,
      reactorCore: "flash_nexus",
    },
    partPositions: {
      weapons: { x: 56, y: 56.5 },
      targeting: { x: 66, y: 56.5 },
      thrusters: { x: 33, y: 51.5 },
    },
    skills: ["rapidFire", "energyBlast"],
  },
  graver: {
    name: "Graver",
    images: {
      ship: graverShipImage,
      pilot: graverPilotImage,
    },
    dialogs: {
      opening:
        "The Core is pretty cool, but I bet it wishes it had more shielding, like me.",
      win: "I didn't like that at all.",
      lose: "Why did you even try, have you SEEN my ship?",
    },
    position: 180,
    heightPercentage: 35,
    medallions: 60,
    baseStatsModifiers: {
      firepower: 2,
      resilience: 4,
      speed: 3,
      precision: 2,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 2,
      precision: 3,
      energy: 1,
    },
    baseStatsTargets: [PRECISION, ENERGY, FIREPOWER, RESILIENCE, SPEED],
    isBoss: false,
    drops: {
      platinum: "fortress_core",
      gold: "castle_core",
      silver: "void_guard",
      bronze: "nexus_rocket",
    },
    installedUpgrades: {
      weapons: null,
      shields: "void_guard",
      thrusters: "nexus_rocket",
      targetingSystem: null,
      reactorCore: "fortress_core",
    },
    partPositions: {
      weapons: { x: 66, y: 64.5 },
      targeting: { x: 76, y: 64.5 },
      thrusters: { x: 12.5, y: 54 },
    },
    skills: ["energyBlast", "emergencyRepairs"],
  },
  wraeth: {
    name: "Wraeth",
    images: {
      ship: wraethShipImage,
      pilot: wraethPilotImage,
    },
    dialogs: {
      opening:
        "I'm not obssessed with our planet's Antimatter Core. I'm just hopelessly in love with it.",
      win: "...I'm going to go stare at the Core for comfort.",
      lose: "Did the Core see me win!? ...Er, I know it's not sentient.",
    },
    position: 220,
    heightPercentage: 35,
    medallions: 80,
    baseStatsModifiers: {
      firepower: 1,
      resilience: 2,
      speed: 3,
      precision: 4,
      energy: 5,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [ENERGY, PRECISION, FIREPOWER, RESILIENCE, SPEED],
    isBoss: false,
    drops: {
      platinum: "hypernova",
      gold: "supernova",
      silver: "star_nexus",
      bronze: "moon_nexus",
    },
    installedUpgrades: {
      weapons: null,
      shields: "void_wall",
      thrusters: "voidburner",
      targetingSystem: "void_scope",
      reactorCore: "hypernova",
    },
    partPositions: {
      weapons: { x: 63, y: 66 },
      targeting: { x: 73, y: 66 },
      thrusters: { x: 25.8, y: 59 },
    },
    skills: ["energyBlast", "emergencyRepairs", "rapidFire"],
  },
};

export default SENECA_HAVEN_MOBS;
